import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/components/Icon";
import { useLiveDealerStatus } from "@/hooks/useLiveDealerStatus";
import { useBetSlip } from "@/context/BetSlipContext";
import FeaturedEventHero from "@/components/sportsbook/FeaturedEventHero";
import DiscoveryGameCard from "@/components/sportsbook/DiscoveryGameCard";
import iconMark from "@/assets/pnrbet-icon.png";
import { LivePill } from "@/components/sportsbook/sb";
import LoadError from "@/components/ui/LoadError";
import PageContainer from "@/components/ui/PageContainer";
import { formatOdds, useOddsFormat } from "@/lib/odds";
import { sortSportsbookEvents, startingSoonList } from "@/lib/sportsbookPriority";
import { useDailyReward } from "@/lib/useDailyReward";
import { dailyRewardLabel, dailyRewardTerms, dailyRewardClaimMessage } from "@/lib/dailyReward";

// Home game shelf — horizontal 300px discovery-card scroller on mobile (SBX-M8),
// a 3-up grid on desktop (SBX-07).
function HomeGameShelf({ events, pick, isPicked }) {
  const list = events.slice(0, 6);
  return (
    <>
      <div className="no-scrollbar -mr-4 flex gap-3 overflow-x-auto pb-1 lg:hidden">
        {list.map((ev) => (
          <div key={ev.id} className="w-[300px] shrink-0"><DiscoveryGameCard event={ev} onPick={pick} isPicked={isPicked} mobile /></div>
        ))}
      </div>
      <div className="hidden gap-4 lg:grid lg:grid-cols-3">
        {list.slice(0, 3).map((ev) => (
          <div key={ev.id}><DiscoveryGameCard event={ev} onPick={pick} isPicked={isPicked} /></div>
        ))}
      </div>
    </>
  );
}

/* PRYMEBET redesign — Home lobby (logged in). Faithful to Claude Design "01 Landing
   & Home / LH-02": greeting, daily-reward banner, feature-card row (welcome offer /
   original / live dealer), Live now, Jump back in (originals), Featured parlays.
   Presentation only — data from AuthContext + /events. */


// Native game artwork (same assets the Casino lobby uses) so the featured card
// shows the right thumbnail per game (Crash → Crash art, etc.).
const IMG = {
  "dragon-tower": "/game-art/dragon-tower.jpeg",
  keno: "/game-art/keno.jpeg",
  plinko: "/game-art/plinko.jpeg",
  dice: "/game-art/dice.jpeg",
  coinflip: "/game-art/coinflip.jpeg",
  crash: "/game-art/crash.jpeg",
  mines: "/game-art/mines.jpeg",
  slots: "/game-art/slots.jpeg",
  roulette: "/game-art/roulette.jpeg",
  blackjack: "/game-art/blackjack.jpeg",
  baccarat: "/game-art/baccarat.jpeg",
  live_dealer: "/game-art/live_dealer.jpeg",
};
const NATIVE_CASINO = [
  { name: "Dragon Tower", img: IMG["dragon-tower"], tag: "PRYMEBET Original" },
  { name: "Keno", img: IMG.keno, tag: "PRYMEBET Original" },
  { name: "Plinko", img: IMG.plinko, tag: "PRYMEBET Original" },
  { name: "Crash", img: IMG.crash, tag: "PRYMEBET Original" },
  { name: "Mines", img: IMG.mines, tag: "PRYMEBET Original" },
  { name: "Dice", img: IMG.dice, tag: "PRYMEBET Original" },
  { name: "Blackjack", img: IMG.blackjack, tag: "PRYMEBET Original" },
  { name: "Slots", img: IMG.slots, tag: "PRYMEBET Original" },
  { name: "Roulette", img: IMG.roulette, tag: "PRYMEBET Original" },
  { name: "Coin Flip", img: IMG.coinflip, tag: "PRYMEBET Original" },
  { name: "Baccarat", img: IMG.baccarat, tag: "PRYMEBET Original" },
];

const pickRandom = (arr) => (arr.length ? arr[Math.floor(Math.random() * arr.length)] : null);

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

// Deep-link slugs so each Casino CTA opens the exact native game (UIA-12). The Casino
// page reads ?game=<slug> and auto-opens that game; Back returns to Home.
const GAME_SLUG = { dragontower: "dragon-tower", "dragon-tower": "dragon-tower", keno: "keno", plinko: "plinko", crash: "crash", mines: "mines", dice: "dice", blackjack: "blackjack", slots: "slots", roulette: "roulette", coinflip: "coinflip", baccarat: "baccarat" };

// §7 one-shot staggered entrance for the Home content sections (reduced-motion guarded by the
// caller — when the user prefers reduced motion we fall back to a static container with no stagger).
const SECTION_STAGGER = { show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } } };
const SECTION_ITEM = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
};

export default function Home() {
  const { user } = useAuth();
  const reduceMotion = useReducedMotion();
  const { anyLive: liveDealerLive } = useLiveDealerStatus();
  const { toggle, has } = useBetSlip();
  const oddsFmt = useOddsFormat();
  const [events, setEvents] = useState([]);
  const {
    bonus, loading: bonusLoading, error: bonusError, claiming,
    claim: claimReward, reload: loadBonus, countdown: liveCd, canClaim, onCooldown,
  } = useDailyReward();
  const [featured, setFeatured] = useState({ casino: [], live: [] });
  // Stale flag so a failed background refresh shows a subtle indicator (UIA-11).
  const [eventsStale, setEventsStale] = useState(false);
  // Distinct FIRST-load failure (UIA-02): /events never loaded, so offer a retry instead of a
  // silent empty board. A later background-refresh failure is just "stale", not this.
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [eventsErr, setEventsErr] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  // Visibility-aware /events polling (UIA-11): refetch every ~25s while the tab is
  // visible so the board reflects latest lines. Selected cards + scroll position are
  // preserved because we only swap the events array (the slip lives in a separate
  // context and card selection is derived from it, not local state).
  useEffect(() => {
    let timer = null;
    const load = () =>
      api.get("/events")
        .then(({ data }) => { setEvents(Array.isArray(data) ? data : []); setEventsStale(false); setEventsLoaded(true); setEventsErr(false); })
        .catch(() => { setEventsStale(true); setEventsErr(true); });
    load();
    const start = () => { if (!timer) timer = setInterval(() => { if (!document.hidden) load(); }, 25000); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => { if (document.hidden) stop(); else { load(); start(); } };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [retryTick]);

  useEffect(() => {
    api.get("/casino/featured")
      .then(({ data }) => setFeatured({ casino: data?.casino || [], live: data?.live || [] }))
      .catch(() => {});
  }, []);

  // A random featured casino game per page load — native originals plus the
  // (deliberately previewed) JackTop titles, each carrying its own thumbnail.
  const casinoPick = useMemo(() => {
    const provider = (featured.casino || [])
      .filter((g) => g.thumbnail_url)
      .map((g) => ({ name: g.name, img: g.thumbnail_url, tag: g.provider }));
    return pickRandom([...NATIVE_CASINO, ...provider]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featured.casino]);

  // Deep-link the Casino feature card to the EXACT native game when the pick is one of our
  // originals (?game=<slug> auto-opens it); provider picks have no native deep-link, so the
  // card falls back to the lobby. Direct destination, never a misleading CTA.
  const casinoSlug = GAME_SLUG[String(casinoPick?.name || "").toLowerCase().replace(/\s+/g, "")] || "";

  // Live-dealer card: a native table when a real dealer is broadcasting; if none
  // of our native live games are up, fall back to a previewed JackTop live game.
  const livePick = useMemo(() => {
    if (liveDealerLive) return { name: "Main Blackjack", sub: "Blackjack & Baccarat tables", img: IMG.live_dealer, badge: "live" };
    const g = pickRandom((featured.live || []).filter((x) => x.thumbnail_url));
    if (g) return { name: g.name, sub: `${g.provider} · live dealer`, img: g.thumbnail_url, badge: "tables" };
    return { name: "Main Blackjack", sub: "Blackjack & Baccarat tables", img: IMG.live_dealer, badge: "offline" };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featured.live, liveDealerLive]);

  // Provider game groups for the casino section — up to 4 per provider (JackTop now;
  // any future provider appears automatically). Combines the casino + live previews.
  const providerGroups = useMemo(() => {
    const all = [...(featured.casino || []), ...(featured.live || [])]
      .filter((g) => g.thumbnail_url && g.provider && g.provider !== "pnr");
    const map = new Map();
    for (const g of all) {
      const list = map.get(g.provider) || [];
      if (list.length < 4 && !list.some((x) => x.id === g.id)) list.push(g);
      map.set(g.provider, list);
    }
    return Array.from(map.entries());
  }, [featured.casino, featured.live]);
  const PROVIDER_LABEL = { jacktop: "JackTop", kerma: "Kerma" };

  const isGameEvent = (e) => !["outright", "prop"].includes(e.event_type) && e.home_team && e.away_team;
  // Live-only and upcoming are separate on Home (bug #8 — Live now must be live only). Both are ordered
  // by canonical sportsbook priority BEFORE any slice, so a single NFL/NBA game is never hidden behind
  // six ITF matches in the first visible shelf (P0-A).
  const live = useMemo(() => sortSportsbookEvents(events.filter((e) => e.status === "live")), [events]);
  const upcoming = useMemo(() => sortSportsbookEvents(events.filter((e) => e.status === "open")), [events]);
  // "Starting soon" = games starting TODAY/soon, ordered by imminence (soonest first) so an imminent
  // non-major game surfaces instead of being hidden behind a major league that's days away. NOT the
  // days-away upcoming slate (that lives on the full board).
  const startingSoon = useMemo(() => startingSoonList(events, 24), [events]);
  const liveCount = live.length;
  // Featured matchup for the Home hero — a real live/open game with markets.
  const heroEvent = useMemo(() =>
    live.find((e) => isGameEvent(e) && (e.outcomes || []).length) ||
    upcoming.find((e) => isGameEvent(e) && (e.outcomes || []).length) || null,
  [live, upcoming]);

  // suggested parlays derived from real events (no fabricated data); keep the outcome so
  // "Add to Bet Slip" really toggles both legs (bug #3).
  const parlays = useMemo(() => {
    const pool = [...live, ...upcoming].filter(isGameEvent);
    const out = [];
    for (let i = 0; i + 1 < pool.length && out.length < 3; i += 2) {
      const legs = [pool[i], pool[i + 1]].map((e) => {
        const fav = (e.outcomes || []).filter((o) => o.market === "moneyline").sort((a, b) => a.odds - b.odds)[0];
        return fav ? { text: fav.label, odds: fav.odds, ev: e, outcome: fav } : null;
      }).filter(Boolean);
      if (legs.length === 2) out.push(legs);
    }
    return out;
  }, [live, upcoming]);

  // Same global bet-slip wiring the Sportsbook uses — tapping a price on a Live-now
  // card adds/removes that exact leg from the shared slip.
  const pick = (event, outcome) => {
    const eventLabel = event.event_type === "outright" && event.title ? event.title : `${event.away_team} @ ${event.home_team}`;
    toggle({
      eventId: event.id, outcomeId: String(outcome.id), label: `${outcome.label}`, odds: outcome.odds,
      marketVersion: outcome.market_version ?? null, line: outcome.line ?? null, side: outcome.side ?? null, receivedAt: outcome.received_at ?? null, providerUpdatedAt: outcome.provider_updated_at ?? null, expiresAt: outcome.expires_at ?? null, 
      eventLabel, market: outcome.market, league: event.league || null, sport: event.sport || null,
      live: event.status === "live",
    });
  };
  const isPicked = (eventId, outcomeId) => has(eventId, outcomeId);

  const claim = async () => {
    try {
      const data = await claimReward();
      if (data) toast.success(dailyRewardClaimMessage(data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Come back later for your next reward.");
    }
  };

  const name = user?.username || "player";
  const streak = user?.vip?.daily_streak;

  return (
    <PageContainer clearBottomNav rhythm={false} data-testid="home-page">
      {/* §7 one-shot staggered entrance wrapper — owns the page's vertical rhythm so the
          sections keep their spacing while entering in a stagger. Reduced motion: static, no stagger. */}
      <motion.div
        className="space-y-6 lg:space-y-8"
        variants={reduceMotion ? undefined : SECTION_STAGGER}
        initial={reduceMotion ? false : "hidden"}
        animate="show"
      >
      {/* Greeting */}
      <motion.div variants={reduceMotion ? undefined : SECTION_ITEM} className="pt-1">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-pnr-green">Home</p>
        <h1 className="mt-1 font-display text-[28px] font-black leading-[1.05] tracking-[-0.02em] text-white sm:text-4xl">
          {greeting()}, <span className="text-meta">{name}</span>
        </h1>
      </motion.div>

      {/* Daily reward — styled like the "Top up & play" card (surface-premium + green glow +
          subtle PCoin watermark cluster). Gated on real can_claim / next_claim_at; claimed →
          a live countdown replaces the Claim button (aligned with mobile Home + Casino). */}
      <motion.div
        variants={reduceMotion ? undefined : SECTION_ITEM}
        className="surface-premium relative overflow-hidden rounded-2xl p-5"
      >
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-pnr-green/[0.12] via-transparent to-transparent" />
        {/* Decorative coin cluster — brand texture; the real claim lives in the button. */}
        <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-70">
          <span className="absolute right-6 top-1/2 flex h-24 w-24 -translate-y-1/2 items-center justify-center rounded-full bg-pnr-green/[0.07] ring-1 ring-pnr-green/20"><Icon name="pnrcoin" size={34} className="text-pnr-green" /></span>
          <span className="absolute right-2 top-7 h-10 w-10 rounded-full bg-pnr-green/[0.06] ring-1 ring-pnr-green/15" />
          <span className="absolute bottom-7 right-28 h-8 w-8 rounded-full bg-pnr-green/[0.06] ring-1 ring-pnr-green/15" />
        </div>
        <div className="relative flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-[72%]">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-pnr-green">Daily reward</p>
            <h3 className="mt-1 font-display text-2xl font-black tracking-tight text-white">{onCooldown ? "Daily reward claimed" : "Free daily reward"}</h3>
            <p className="mt-1 text-sm text-meta">
              {bonusError ? "Unable to load your reward. Please retry." : bonus
                ? <>{canClaim ? "Claim " : "Next reward: "}<b className="tabular-nums text-white">{dailyRewardLabel(bonus)}</b> every 24h</>
                : "Loading your daily reward…"}
              {streak ? <> · day {streak} streak</> : null}
            </p>
            {bonus && <p className="mt-1 text-xs text-meta">{dailyRewardTerms(bonus)}</p>}
          </div>
          {!onCooldown ? (
            <button
              data-testid="home-claim-reward"
              onClick={bonusError ? loadBonus : claim}
              disabled={claiming || bonusLoading || (!canClaim && !bonusError)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-pnr-green px-6 py-3 font-bold text-surface-1 shadow-[0_6px_18px_rgba(0,214,50,0.28)] transition-colors hover:bg-pnr-green-hover focus-ring disabled:cursor-not-allowed disabled:bg-pnr-green/40 disabled:text-surface-1/60 sm:w-auto"
            >
              <Icon name="pnrcoin" size={16} /> {bonusError ? "Retry reward" : bonusLoading ? "Loading…" : claiming ? "Claiming…" : "Claim"}
            </button>
          ) : (
            /* Claimed → a live countdown REPLACES the Claim button (same behavior on mobile + Casino). */
            <div data-testid="home-claim-countdown" aria-label={`Next reward in ${liveCd}`}
              className="inline-flex w-full items-center gap-2 rounded-full border border-white/10 bg-black/40 px-5 py-2.5 sm:w-auto">
              <Icon name="clock" size={16} className="text-pnr-green" />
              <span className="flex flex-col leading-tight">
                <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-meta">Next drop in</span>
                <span className="font-display text-sm font-extrabold tabular-nums text-white">{liveCd || "soon"}</span>
              </span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Feature-card row: Welcome offer / Refer (post first purchase) · Casino · Live Dealer */}
      <motion.div variants={reduceMotion ? undefined : SECTION_ITEM} className="grid grid-cols-1 gap-4 lg:grid-cols-[1.5fr_1fr_1fr]">
        {user?.first_purchase_done ? (
          <Link
            to="/rewards#refer"
            data-testid="home-refer-card"
            className="surface-premium group relative flex min-h-[190px] flex-col justify-end overflow-hidden rounded-2xl p-5 transition-all hover:-translate-y-0.5 hover:shadow-[var(--elev-3)] focus-ring"
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-pnr-green/[0.10] via-transparent to-transparent" />
            <div className="relative">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-pnr-green">Refer a friend</p>
              <h3 className="mt-1 font-display text-2xl font-black tracking-tight text-white">Invite friends, earn rewards</h3>
              <p className="mt-1 text-sm text-meta">Earn a reward for every friend who joins and plays.</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-white group-hover:text-pnr-green">
                Share your code <Icon name="arrow-right" size={15} />
              </span>
            </div>
          </Link>
        ) : (
          /* First-purchase prompt — a TRUTHFUL coin-pack entry point (no fabricated "500 free
             coins" / "Terms TBD"). Free daily drops are real (the claim banner above), so we
             point at the real store rather than inventing a sign-up grant amount. */
          <Link
            to="/store"
            data-testid="home-getcoins-card"
            className="surface-premium group relative flex min-h-[190px] flex-col justify-end overflow-hidden rounded-2xl p-5 transition-all hover:-translate-y-0.5 hover:shadow-[var(--elev-3)] focus-ring"
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-pnr-green/[0.10] via-transparent to-transparent" />
            {/* Decorative coin cluster — brand texture only, carries no claim. */}
            <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-70">
              <span className="absolute right-5 top-1/2 flex h-24 w-24 -translate-y-1/2 items-center justify-center rounded-full bg-pnr-green/[0.07] ring-1 ring-pnr-green/20"><Icon name="pnrcoin" size={30} className="text-pnr-green" /></span>
              <span className="absolute right-1 top-7 h-10 w-10 rounded-full bg-pnr-green/[0.06] ring-1 ring-pnr-green/15" />
              <span className="absolute bottom-8 right-24 h-8 w-8 rounded-full bg-pnr-green/[0.06] ring-1 ring-pnr-green/15" />
            </div>
            <div className="relative max-w-[62%]">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-pnr-green">Get coins</p>
              <h3 className="mt-1 font-display text-2xl font-black tracking-tight text-white">Top up &amp; play</h3>
              <p className="mt-1 text-sm text-meta">Pick a coin pack to play the sportsbook and casino. Free daily drops too.</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-white group-hover:text-pnr-green">
                See coin packs <Icon name="arrow-right" size={15} />
              </span>
            </div>
          </Link>
        )}

        <Link
          to={casinoSlug ? `/casino?game=${casinoSlug}` : "/casino"}
          data-testid="home-casino-card"
          className="surface-premium group relative flex min-h-[190px] flex-col justify-between overflow-hidden rounded-2xl p-5 transition-all hover:-translate-y-0.5 hover:shadow-[var(--elev-3)] focus-ring"
        >
          {casinoPick?.img && (
            <img src={casinoPick.img} alt="" loading="lazy"
              className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-500 group-hover:scale-105" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20" />
          <div className="relative flex items-center justify-between">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-pnr-green">Casino</p>
            <Icon name="originals" size={20} className="text-pnr-green" />
          </div>
          <div className="relative">
            <h3 className="font-display text-xl font-black tracking-tight text-white drop-shadow">{casinoPick?.name || "Casino"}</h3>
            <p className="mt-1 text-sm text-meta-strong">{casinoPick?.tag || "Provably fair"}</p>
            <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-white group-hover:text-pnr-green">
              Play now <Icon name="arrow-right" size={15} />
            </span>
          </div>
        </Link>

        <Link
          to="/casino?tab=live"
          data-testid="home-live-card"
          className="surface-premium group relative flex min-h-[190px] flex-col justify-between overflow-hidden rounded-2xl p-5 transition-all hover:-translate-y-0.5 hover:shadow-[var(--elev-3)] focus-ring"
        >
          {livePick?.img && (
            <img src={livePick.img} alt="" loading="lazy"
              className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-500 group-hover:scale-105" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20" />
          <div className="relative flex items-center justify-between">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-white/70">Live dealer</p>
            {livePick.badge === "live" ? (
              <span className="flex items-center gap-1 rounded-full bg-pnr-red/15 px-2 py-0.5 text-[10px] font-bold text-pnr-red">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-pnr-red" /> LIVE
              </span>
            ) : livePick.badge === "tables" ? (
              <span className="flex items-center gap-1 rounded-full bg-pnr-green/15 px-2 py-0.5 text-[10px] font-bold text-pnr-green">
                <span className="h-1.5 w-1.5 rounded-full bg-pnr-green" /> TABLES
              </span>
            ) : (
              <span className="flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold text-white/55">
                <span className="h-1.5 w-1.5 rounded-full bg-white/30" /> OFFLINE
              </span>
            )}
          </div>
          <div className="relative">
            <h3 className="font-display text-xl font-black tracking-tight text-white drop-shadow">{livePick?.name || "Main Blackjack"}</h3>
            <p className="mt-1 text-sm text-meta-strong">{livePick?.sub || "Blackjack & Baccarat tables"}</p>
            <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-white group-hover:text-pnr-green">
              Take a seat <Icon name="arrow-right" size={15} />
            </span>
          </div>
        </Link>
      </motion.div>

      {/* Featured matchup — desktop hero (300px promo) / mobile discovery card (SBX-07 / M8) */}
      {heroEvent && (
        <motion.section variants={reduceMotion ? undefined : SECTION_ITEM} data-testid="home-featured-matchup">
          <div className="hidden lg:block">
            <FeaturedEventHero event={heroEvent} onPick={pick} isPicked={isPicked} freeBets={user?.free_bets?.length || 0} promo={null} home />
          </div>
          <div className="lg:hidden">
            <p className="mb-2 text-[10px] font-extrabold uppercase tracking-[0.2em] text-pnr-green">Featured matchup</p>
            <DiscoveryGameCard event={heroEvent} onPick={pick} isPicked={isPicked} mobile />
          </div>
        </motion.section>
      )}

      {/* Live now — horizontal shelf on mobile, grid on desktop; LIVE only (bug #8) */}
      <motion.section variants={reduceMotion ? undefined : SECTION_ITEM} className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-3 font-display text-xl font-black tracking-tight text-white sm:text-2xl">
            Live now {liveCount > 0 ? <LivePill count={liveCount} /> : null}
          </h2>
          <Link to="/sportsbook" className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-surface-border bg-surface-4 px-3 py-1.5 text-sm font-bold text-pnr-green transition-colors hover:border-pnr-green/50 hover:bg-pnr-green/10 focus-ring">
            Sportsbook <Icon name="arrow-right" size={14} />
          </Link>
        </div>
        {live.filter(isGameEvent).length ? (
          <HomeGameShelf events={live.filter(isGameEvent)} pick={pick} isPicked={isPicked} />
        ) : (eventsErr && !eventsLoaded) ? (
          <LoadError onRetry={() => setRetryTick((t) => t + 1)} message="We couldn't load the latest games." testid="home-events-error" />
        ) : (
          <div className="rounded-2xl border border-dashed border-surface-border bg-surface-3 p-8 text-center text-sm text-meta" data-testid="home-no-live">
            No games are live right now — <Link to="/sportsbook" className="rounded font-semibold text-pnr-green focus-ring">see upcoming games</Link>.
          </div>
        )}
      </motion.section>

      {/* Starting soon — games starting today/soon, soonest first (not the days-away slate) */}
      {startingSoon.filter(isGameEvent).length > 0 && (
        <motion.section variants={reduceMotion ? undefined : SECTION_ITEM} className="space-y-4">
          <h2 className="font-display text-xl font-black tracking-tight text-white sm:text-2xl">Starting soon</h2>
          <HomeGameShelf events={startingSoon.filter(isGameEvent)} pick={pick} isPicked={isPicked} />
        </motion.section>
      )}

      {/* Featured parlays — "Add to Bet Slip" really toggles both legs (bug #3) */}
      {parlays.length > 0 && (
        <motion.section variants={reduceMotion ? undefined : SECTION_ITEM} className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="font-display text-xl font-black tracking-tight text-white sm:text-2xl">Featured parlays</h2>
              <p className="mt-0.5 text-sm text-meta">Built from the favourite in two games · priced live</p>
            </div>
            <Link to="/sportsbook" className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-surface-border bg-surface-4 px-3 py-1.5 text-sm font-bold text-pnr-green transition-colors hover:border-pnr-green/50 hover:bg-pnr-green/10 focus-ring">
              Build your own <Icon name="arrow-right" size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            {parlays.map((legs, i) => {
              const combined = legs.reduce((acc, l) => acc * (l.odds || 1), 1);
              const onSlip = legs.every((l) => has(l.ev.id, String(l.outcome.id)));
              const addParlay = () => legs.forEach((l) => { if (!has(l.ev.id, String(l.outcome.id))) pick(l.ev, l.outcome); });
              const title = legs.map((l) => String(l.text || "").trim().split(/\s+/).slice(-1)[0]).join(" + ");
              const cardStyle = { background: "linear-gradient(145deg, hsl(var(--ink-700)) 0%, hsl(var(--surface-3)) 45%, hsl(var(--ink-800)) 100%)", boxShadow: "inset 0 1px 0 hsl(var(--edge-light)), inset 0 -1px 0 hsl(var(--edge-dark)), var(--elev-2)", border: "1px solid hsl(var(--surface-border))" };
              const greenTint = { background: "rgba(0,214,50,.12)", color: "#00D632", boxShadow: "inset 0 0 0 1px rgba(0,214,50,.35)" };
              return (
                <React.Fragment key={i}>
                  {/* desktop card */}
                  <div className="hidden flex-col gap-3 rounded-[18px] p-4 lg:flex" style={cardStyle} data-testid={`home-parlay-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-meta">{legs.length}-leg parlay</span>
                      <span className="rounded-md px-2 py-0.5 font-display text-[13px] font-extrabold tabular-nums" style={greenTint}>{formatOdds(combined, oddsFmt)}</span>
                    </div>
                    <div className="space-y-2">
                      {legs.map((l, j) => (
                        <div key={j} className="flex items-start justify-between gap-2 text-sm">
                          <span className="min-w-0">
                            <span className="block truncate font-semibold text-white">{l.text}</span>
                            <span className="block truncate text-[11px] text-meta">{`${l.ev.away_team} @ ${l.ev.home_team}`}</span>
                          </span>
                          <span className="shrink-0 font-display font-extrabold tabular-nums text-meta">{formatOdds(l.odds, oddsFmt)}</span>
                        </div>
                      ))}
                    </div>
                    <button onClick={addParlay} disabled={onSlip} data-testid={`home-parlay-add-${i}`}
                      className="mt-auto flex items-center justify-between gap-2 rounded-xl px-3.5 py-2.5 text-left transition-colors focus-ring"
                      style={onSlip ? greenTint : { background: "#00D632", color: "#0A0A0A" }}>
                      <span className="flex items-center gap-1.5 font-display text-[13px] font-extrabold">{onSlip && <Icon name="check" size={13} />}{onSlip ? "On your Bet Slip" : "Add to Bet Slip"}</span>
                      <span className="text-[11px] font-bold" style={{ color: onSlip ? "#00D632" : "rgba(10,10,10,.7)" }}>$10 pays ${(10 * combined).toFixed(2)}</span>
                    </button>
                  </div>
                  {/* mobile compact row (SBX-M8) */}
                  <button onClick={addParlay} disabled={onSlip} data-testid={`home-parlay-m-${i}`}
                    className="flex items-center gap-3 rounded-[16px] p-3.5 text-left focus-ring lg:hidden" style={cardStyle}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-display text-[15px] font-extrabold text-white">{title}</span>
                      <span className="block truncate text-xs text-meta">{legs.length}-leg parlay · $10 pays ${(10 * combined).toFixed(2)}</span>
                    </span>
                    <span className="flex h-11 shrink-0 items-center gap-1 rounded-xl px-3 font-display text-[13px] font-extrabold tabular-nums" style={greenTint}>
                      {onSlip ? <Icon name="check" size={14} /> : <Icon name="plus" size={14} />}{formatOdds(combined, oddsFmt)}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </motion.section>
      )}

      {/* Casino — premium section (LH-02): featured card + tiles, added below sportsbook */}
      <motion.section variants={reduceMotion ? undefined : SECTION_ITEM} className="space-y-4" data-testid="home-casino-section">
        <div className="flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2.5 font-display text-xl font-black tracking-tight text-white sm:text-2xl">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-pnr-green/12 ring-1 ring-pnr-green/20"><Icon name="casino" size={18} className="text-pnr-green" /></span>
            Casino
          </h2>
          <Link to="/casino" className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-surface-border bg-surface-4 px-3 py-1.5 text-sm font-bold text-pnr-green transition-colors hover:border-pnr-green/50 hover:bg-pnr-green/10 focus-ring">
            All games <Icon name="arrow-right" size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
          {/* featured card — deep-links to the exact game (UIA-12) */}
          <Link to={`/casino?game=${GAME_SLUG.mines}`} data-testid="home-casino-featured"
            className="group relative col-span-2 flex aspect-[3/2] flex-col justify-end overflow-hidden rounded-2xl ring-1 ring-white/5 transition-all hover:-translate-y-1 hover:ring-2 hover:ring-pnr-green/50 focus-ring">
            <img src={IMG.mines} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm"><img src={iconMark} alt="" className="h-3.5 w-3.5 rounded-[3px] object-contain" /><span className="text-pnr-green">PRYMEBET Original</span></span>
            <div className="relative p-4">
              <h3 className="font-display text-2xl font-black tracking-tight text-white drop-shadow">Mines</h3>
              <p className="mt-0.5 text-sm text-white/70">Provably fair · cash out anytime</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-pnr-green px-5 py-2 text-sm font-bold text-surface-1 transition-colors group-hover:bg-pnr-green-hover">Play Mines</span>
                <span className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-bold text-white">All originals</span>
              </div>
            </div>
          </Link>
          {/* four tiles */}
          {[["crash", "Crash", IMG.crash], ["dice", "Dice", IMG.dice], ["blackjack", "Blackjack", IMG.blackjack], ["slots", "Slots", IMG.slots]].map(([id, label, img]) => (
            <Link key={id} to={GAME_SLUG[id] ? `/casino?game=${GAME_SLUG[id]}` : "/casino"}
              className="group relative aspect-[3/4] overflow-hidden rounded-2xl ring-1 ring-white/5 transition-all hover:-translate-y-1 hover:ring-2 hover:ring-pnr-green/60 focus-ring">
              <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/55 to-transparent" />
              <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-md bg-black/55 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] backdrop-blur-sm"><img src={iconMark} alt="" className="h-3.5 w-3.5 rounded-[3px] object-contain" /><span className="text-pnr-green">Original</span></span>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-2.5 pt-8">
                <p className="font-display text-sm font-black tracking-tight text-white drop-shadow">{label}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* Provider game rows — up to 4 per provider (JackTop today; future providers auto-appear) */}
        {providerGroups.map(([provider, games]) => (
          <div key={provider} className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-display text-base font-black tracking-tight text-white">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-white/[0.06]"><Icon name="dice" size={13} className="text-meta" /></span>
                {PROVIDER_LABEL[provider] || provider}
              </h3>
              <Link to="/casino" className="inline-flex items-center gap-1 rounded-full text-xs font-bold text-pnr-green hover:text-pnr-green-hover focus-ring">
                All games <Icon name="arrow-right" size={13} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {games.map((g) => (
                <Link key={g.id} to="/casino"
                  /* Provider art (JackTop) ships 1:1 — a square tile crops nothing (no cut-off
                     dealer heads), unlike the old 3:4 portrait frame. */
                  className="group relative aspect-square overflow-hidden rounded-2xl ring-1 ring-white/5 transition-all hover:-translate-y-1 hover:ring-2 hover:ring-pnr-green/60 focus-ring">
                  <img src={g.thumbnail_url} alt="" className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/55 to-transparent" />
                  <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-md bg-black/55 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-white/85 backdrop-blur-sm">{PROVIDER_LABEL[provider] || provider}</span>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-2.5 pt-8">
                    <p className="truncate font-display text-sm font-black tracking-tight text-white drop-shadow">{g.name}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </motion.section>
      </motion.div>
    </PageContainer>
  );
}

